import React from 'react'
import { Link } from 'react-router-dom'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const SECTIONS = [
  {
    title: '1. Information We Collect',
    content: `We collect information you provide directly to us when you create an account, place an order, or communicate with us. This includes:\n\n• Personal identification information (name, email address, phone number)\n• Address and location information for signing appointments\n• Payment information (processed securely through Stripe)\n• Professional credentials for notary signing agents (commission certificates, E&O insurance, background check results, government ID, W-9)\n• Communications you send to us\n• Device and usage information collected automatically when you use our platform`
  },
  {
    title: '2. How We Use Your Information',
    content: `We use the information we collect to:\n\n• Provide, maintain, and improve our services\n• Match clients with appropriate notary signing agents\n• Process payments and send payment confirmations\n• Send service-related communications and notifications\n• Verify notary credentials and maintain compliance records\n• Generate audit reports for title companies as required\n• Comply with legal obligations\n• Prevent fraud and ensure platform security\n• Respond to your requests and provide customer support`
  },
  {
    title: '3. Information Sharing',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:\n\n• With notary signing agents assigned to your order (limited to information needed to complete the signing)\n• With title companies or lenders who are clients of NSV for audit and compliance purposes\n• With payment processors (Stripe) to process transactions\n• With service providers who assist us in operating our platform\n• When required by law, court order, or governmental authority\n• To protect the rights and safety of NSV and our users`
  },
  {
    title: '4. Gramm-Leach-Bliley Act (GLBA) Compliance',
    content: `As a service provider in the financial services industry, we comply with the Gramm-Leach-Bliley Act. We implement appropriate safeguards to protect non-public personal financial information (NPI) that we receive in connection with loan signing services. All notary signing agents on our platform are required to maintain the confidentiality of borrower information and are prohibited from disclosing NPI to unauthorized parties.`
  },
  {
    title: '5. Data Security',
    content: `We implement industry-standard security measures to protect your information including:\n\n• Encrypted data transmission (SSL/TLS)\n• Secure cloud storage with Amazon Web Services (S3) encryption\n• Row-level security on our database (Supabase)\n• Access controls limiting data access to authorized personnel only\n• Automatic deletion of sensitive documents after 60 days\n\nWhile we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but commit to notifying you promptly in the event of a data breach.`
  },
  {
    title: '6. Data Retention',
    content: `We retain your personal information for as long as your account is active or as needed to provide services. Uploaded signing documents are automatically deleted after 60 days following completion of services. Compliance and audit records may be retained longer as required by applicable law. You may request deletion of your account and associated data at any time by contacting us.`
  },
  {
    title: '7. Your Rights',
    content: `You have the right to:\n\n• Access the personal information we hold about you\n• Correct inaccurate or incomplete information\n• Request deletion of your personal information\n• Opt out of non-essential communications\n• Request a copy of your data in a portable format\n\nTo exercise these rights, contact us at nancy@notarysigningvault.com. We will respond to all requests within 30 days.`
  },
  {
    title: '8. Cookies and Tracking',
    content: `Our platform uses essential cookies necessary for authentication and platform functionality. We do not use advertising cookies or sell your browsing data to advertisers. You may disable cookies in your browser settings, though this may affect certain features of our platform.`
  },
  {
    title: '9. Children\'s Privacy',
    content: `Our Service is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately and we will take steps to delete it.`
  },
  {
    title: '10. Third-Party Links',
    content: `Our platform may contain links to third-party websites. We are not responsible for the privacy practices of those sites and encourage you to review their privacy policies. This Privacy Policy applies only to information collected by Notary Signing Vault, LLC.`
  },
  {
    title: '11. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify registered users of material changes via email and update the "Last Updated" date at the top of this page. Your continued use of our Service after such changes constitutes acceptance of the updated policy.`
  },
  {
    title: '12. Contact Us',
    content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:\n\nNotary Signing Vault, LLC\nNancy Phillips, Founder & CEO\nJacksonville, Florida\nPhone: (904) 865-0133\nEmail: nancy@notarysigningvault.com\nWebsite: notarysigningvault.com`
  },
]

export default function PrivacyPolicy() {
  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    :focus-visible { outline: 2px solid #4D4E8D; outline-offset: 3px; border-radius: 6px; }
    .pp-hero { background: #E9EAEC; padding: 56px 24px 36px; text-align: center; font-family:'Plus Jakarta Sans',sans-serif; }
    .pp-hero h1 { font-family:'Plus Jakarta Sans',sans-serif; font-size: clamp(30px,3.6vw,40px); font-weight: 800; color: #151715; margin-bottom: 8px; letter-spacing:-.025em; }
    .pp-hero-sub { font-size: 13px; color: #8A8F94; font-weight:500; }
    .pp-hero-gold { display: block; width: 44px; height: 3px; background: #4D4E8D; border-radius:2px; margin: 16px auto; }
    .pp-body { background: #E9EAEC; min-height: 100vh; font-family:'Plus Jakarta Sans',sans-serif; }
    .pp-wrap { max-width: 760px; margin: 0 auto; padding: 16px 24px 72px; }
    .pp-section { margin-bottom: 30px; }
    .pp-section h2 { font-size: 16px; font-weight: 800; color: #151715; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #4D4E8D; display: inline-block; letter-spacing:-.01em; }
    .pp-section p { font-size: 14px; color: #5C6065; font-weight:500; line-height: 1.8; white-space: pre-line; }
    @media(max-width:640px){ .pp-hero h1 { font-size: 26px; } }
  `

  return (
    <div style={{ background: '#E9EAEC', minHeight: '100vh' }}>
      <style>{styles}</style>

      <SiteHeader />

      {/* Hero */}
      <div className="pp-hero">
        <div style={{ fontSize: 10, letterSpacing: '0.2em', color: '#4D4E8D', marginBottom: 12 }}>LEGAL</div>
        <span className="pp-hero-gold" aria-hidden="true" />
        <h1>Privacy Policy</h1>
        <p className="pp-hero-sub">Effective Date: April 18, 2026 · Last Updated: April 18, 2026</p>
      </div>

      {/* Body */}
      <div className="pp-body" id="nsv-main">
        <div className="pp-wrap">

          {/* Intro */}
          <div style={{ background: '#fff', border: '1px solid #E2E4E7', borderRadius: 14, padding: '20px 24px', marginBottom: 32 }}>
            <p style={{ fontSize: 14, color: '#3A3D40', lineHeight: 1.8 }}>
              At Notary Signing Vault, LLC, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. As a service provider in the financial services industry, we comply with applicable privacy laws including the Gramm-Leach-Bliley Act (GLBA).
            </p>
          </div>

          {/* Sections */}
          {SECTIONS.map((s, i) => (
            <div key={i} className="pp-section">
              <h2>{s.title}</h2>
              <p>{s.content}</p>
            </div>
          ))}

          {/* Signature */}
          <div style={{ background: '#EEF0F8', border:'1px solid #C7CBE2', borderRadius: 14, padding: '22px 24px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight:800, color: '#4D4E8D', marginBottom: 4 }}>Notary Signing Vault, LLC</div>
            <div style={{ fontSize: 12, fontWeight:500, color: '#5C6065' }}>Nancy Phillips — Founder & CEO · Jacksonville, Florida</div>
            <div style={{ fontSize: 11, fontWeight:600, color: '#5E8A72', marginTop: 8 }}>Veteran & Hispanic Owned Business</div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  )
}
